import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { motion } from "framer-motion";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { Eye, EyeSlash, EnvelopeSimple, Lock, ArrowsClockwise } from "@phosphor-icons/react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGO_URL = "/dolphin-leadflow-logo.png";
const BG_URL = "https://customer-assets.emergentagent.com/job_lead-verifier/artifacts/i05v5o5k_Firefly.jpg";

const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotSubmitting, setForgotSubmitting] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || "/dashboard";

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Please fill in all fields");
      return;
    }
    
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back!");
      navigate(from, { replace: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  const openForgotDialog = () => {
    setForgotEmail(email);
    setForgotSent(false);
    setForgotOpen(true);
  };

  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    const normalized = (forgotEmail || "").trim();
    if (!normalized) {
      toast.error("Enter your email");
      return;
    }
    setForgotSubmitting(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: normalized });
      setForgotSent(true);
    } catch (error) {
      // Backend always returns 200 to prevent email enumeration; if a real error happens just show generic
      toast.error("Couldn't send reset email. Please try again.");
    } finally {
      setForgotSubmitting(false);
    }
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center bg-cover bg-center bg-no-repeat"
      style={{ backgroundImage: `url(${BG_URL})` }}
    >
      {/* Overlay */}
      <div className="absolute inset-0 bg-black/60" />
      
      {/* Login Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative z-10 w-full max-w-md mx-4"
      >
        <div className="bg-black/50 backdrop-blur-xl border border-white/10 rounded-2xl p-8 shadow-2xl">
          {/* Logo */}
          <div className="text-center mb-6">
            <img src={LOGO_URL} alt="Dolphin LeadFlow" className="h-32 mx-auto mb-4" />
          </div>

          {/* Welcome Back Title */}
          <h1 className="text-4xl font-black text-white text-center mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Welcome Back
          </h1>
          <p className="text-zinc-400 text-center mb-8">Sign in to your account to continue</p>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-zinc-400 text-sm">Email</Label>
              <div className="relative">
                <EnvelopeSimple className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" size={18} />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  className="pl-10 bg-zinc-900/70 border-zinc-700 focus:border-[#00D4FF] focus:ring-1 focus:ring-[#00D4FF] text-white placeholder:text-zinc-500 h-12 rounded-lg"
                  data-testid="login-email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-zinc-400 text-sm">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" size={18} />
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="pl-10 pr-10 bg-zinc-900/70 border-zinc-700 focus:border-[#00D4FF] focus:ring-1 focus:ring-[#00D4FF] text-white placeholder:text-zinc-500 h-12 rounded-lg"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-white transition-colors"
                  data-testid="toggle-password-btn"
                >
                  {showPassword ? <EyeSlash size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#006994] hover:bg-[#005A80] text-white font-bold h-12 rounded-lg transition-all duration-200 mt-6"
              data-testid="login-submit-btn"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
                  Signing in...
                </span>
              ) : (
                "Sign in"
              )}
            </Button>
            <div className="text-center">
              <button
                type="button"
                onClick={openForgotDialog}
                className="text-sm text-slate-500 hover:text-[#006994] transition-colors"
                data-testid="forgot-password-link"
              >
                Forgot your password?
              </button>
            </div>
            <p className="text-center text-xs text-slate-500 mt-4">Dolphin Digital OOH Media &copy; 2026</p>
          </form>
        </div>
      </motion.div>

      {/* Forgot Password Dialog */}
      <Dialog open={forgotOpen} onOpenChange={(open) => { setForgotOpen(open); if (!open) { setForgotSent(false); }}}>
        <DialogContent className="bg-white border-slate-200" data-testid="forgot-password-dialog">
          <DialogHeader>
            <DialogTitle className="text-slate-900">Reset your password</DialogTitle>
            <DialogDescription className="text-slate-500">
              {forgotSent
                ? "If that email is registered, you'll receive a reset link shortly. The link expires in 15 minutes."
                : "Enter your account email and we'll send you a reset link."}
            </DialogDescription>
          </DialogHeader>

          {!forgotSent ? (
            <form onSubmit={handleForgotSubmit}>
              <div className="space-y-2 py-2">
                <Label className="text-slate-700">Email</Label>
                <div className="relative">
                  <EnvelopeSimple className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" size={18} />
                  <Input
                    type="email"
                    autoFocus
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="pl-10 bg-white/50 border-slate-200 text-slate-900"
                    data-testid="forgot-email-input"
                  />
                </div>
              </div>
              <DialogFooter className="mt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setForgotOpen(false)}
                  className="border-slate-200 text-slate-700 hover:bg-slate-100"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={forgotSubmitting || !forgotEmail.trim()}
                  className="bg-[#006994] hover:bg-[#005A80] text-white font-bold"
                  data-testid="forgot-submit-btn"
                >
                  {forgotSubmitting ? (
                    <span className="flex items-center gap-2"><ArrowsClockwise size={14} className="animate-spin" /> Sending...</span>
                  ) : (
                    "Send reset link"
                  )}
                </Button>
              </DialogFooter>
            </form>
          ) : (
            <div className="space-y-3 py-2">
              <div className="rounded-lg bg-emerald-500/10 border border-emerald-500/20 p-4">
                <p className="text-sm text-emerald-300">
                  Check your inbox at <span className="font-mono text-slate-900">{forgotEmail}</span>. The reset link expires in 15 minutes.
                </p>
              </div>
              <p className="text-xs text-slate-500">
                Didn't get it? Check your spam folder, or close this dialog and try again.
              </p>
              <DialogFooter>
                <Button
                  onClick={() => setForgotOpen(false)}
                  className="bg-[#006994] hover:bg-[#005A80] text-white font-bold"
                  data-testid="forgot-done-btn"
                >
                  Done
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LoginPage;
